import React from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Button, CircularProgress, Typography } from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import RefreshIcon from '@mui/icons-material/Refresh'
import { colors } from '../../const/colors'
import LogoCircle from '../Shared/LogoCircle'
import MaintenanceIllustration from './MaintenanceIllustration'

export const OVERLAY_ATTRIBUTE = 'data-maintenance-overlay'

const Assurance = ({ label }: { label: string }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '11px' }}>
    <Box
      sx={{
        width: 22,
        height: 22,
        borderRadius: '50%',
        background: colors.greenBg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <CheckIcon sx={{ fontSize: 13, color: colors.green }} />
    </Box>
    <Typography sx={{ fontSize: '13px', lineHeight: 1.5, color: colors.textSecondary }}>
      {label}
    </Typography>
  </Box>
)

const MaintenanceView = ({
  message,
  onRefresh,
  refreshing,
  checkedAt,
}: {
  message: string
  onRefresh: () => void
  refreshing: boolean
  checkedAt: Date | null
}) => {
  const { t, i18n } = useTranslation(['maintenance_screen', 'btn'])

  const paragraphs = message
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean)

  const lines = paragraphs.length
    ? paragraphs
    : [`${t('maintenance_screen:content')}`]

  const lastTry = checkedAt
    ? checkedAt.toLocaleTimeString(
        i18n.language.slice(0, 2) === 'en' ? 'en-GB' : 'pl',
        { hour: '2-digit', minute: '2-digit' },
      )
    : ''

  return (
    <Box
      {...{ [OVERLAY_ATTRIBUTE]: '' }}
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="maintenance-title"
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: { xs: '20px 16px', md: '40px' },
        background: 'rgba(244,247,249,0.94)',
        backdropFilter: 'blur(6px)',
        overflowY: 'auto',
        overscrollBehavior: 'contain',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '-180px',
          left: '-120px',
          width: 520,
          height: 520,
          borderRadius: '50%',
          background:
            'radial-gradient(circle, rgba(19,54,73,0.10) 0%, rgba(19,54,73,0) 70%)',
          pointerEvents: 'none',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '-200px',
          right: '-140px',
          width: 560,
          height: 560,
          borderRadius: '50%',
          background:
            'radial-gradient(circle, rgba(237,189,1,0.14) 0%, rgba(237,189,1,0) 70%)',
          pointerEvents: 'none',
        }}
      />

      <Box
        sx={{
          position: 'relative',
          width: '100%',
          maxWidth: 1060,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1.05fr 1fr' },
          background: colors.surface,
          border: `1px solid ${colors.border}`,
          borderRadius: colors.radiusHero,
          boxShadow: '0 34px 74px -44px rgba(19,54,73,.55)',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: '20px', md: '28px' },
            padding: { xs: '30px 28px', md: '40px 40px 36px' },
            background: `linear-gradient(120deg,${colors.navyHero} 0%,${colors.navyDark} 58%)`,
            color: '#fff',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: '-110px',
              left: '-80px',
              width: 300,
              height: 300,
              borderRadius: '50%',
              background:
                'radial-gradient(circle, rgba(237,189,1,0.30) 0%, rgba(237,189,1,0) 70%)',
              pointerEvents: 'none',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: '-130px',
              right: '-90px',
              width: 320,
              height: 320,
              borderRadius: '50%',
              background:
                'radial-gradient(circle, rgba(143,212,155,0.26) 0%, rgba(143,212,155,0) 70%)',
              pointerEvents: 'none',
            }}
          />

          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
            }}
          >
            <LogoCircle size={44} />
            <Typography
              sx={{ fontSize: '20px', fontWeight: 400, letterSpacing: '-0.01em' }}
            >
              {'My '}
              <Box component="span" sx={{ fontWeight: 800 }}>
                Olivia
              </Box>
            </Typography>
            <Typography
              sx={{
                fontSize: '11px',
                letterSpacing: '0.06em',
                fontWeight: 600,
                color: 'rgba(255,255,255,0.5)',
                borderLeft: '1px solid rgba(255,255,255,0.16)',
                paddingLeft: '12px',
                marginLeft: '4px',
                textTransform: 'uppercase',
              }}
            >
              {t('maintenance_screen:app_name')}
            </Typography>
          </Box>

          <Box
            sx={{
              position: 'relative',
              flexGrow: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <MaintenanceIllustration />
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: '20px',
            padding: { xs: '32px 26px 36px', md: '48px 44px' },
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <Typography
              id="maintenance-title"
              component="h1"
              sx={{
                margin: 0,
                fontSize: { xs: '23px', md: '28px' },
                fontWeight: 800,
                letterSpacing: '-0.02em',
                lineHeight: 1.16,
                color: colors.textBody,
              }}
            >
              {t('maintenance_screen:title')}
            </Typography>
            {lines.map((line, index) => (
              <Typography
                key={`${index}`}
                data-e2e="maintenance-message"
                sx={{
                  fontSize: '13.5px',
                  lineHeight: 1.65,
                  color: colors.textSecondary,
                }}
              >
                {line}
              </Typography>
            ))}
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '11px' }}>
            <Assurance label={t('maintenance_screen:nothing_to_do')} />
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '9px' }}>
            <Button
              onClick={onRefresh}
              disabled={refreshing}
              fullWidth
              disableElevation
              startIcon={
                refreshing ? undefined : <RefreshIcon sx={{ fontSize: 18 }} />
              }
              sx={{
                height: 50,
                borderRadius: '13px',
                textTransform: 'none',
                fontSize: '15px',
                fontWeight: 700,
                color: '#fff',
                background: `linear-gradient(135deg,${colors.navyHero} 0%,${colors.navy} 55%)`,
                boxShadow: colors.shadowBtn,
                '&:hover': {
                  background: `linear-gradient(135deg,${colors.navy} 0%,${colors.navyDark} 55%)`,
                  boxShadow: colors.shadowBtn,
                },
                '&.Mui-disabled': {
                  background: colors.slate,
                  color: 'rgba(255,255,255,0.8)',
                },
              }}
            >
              {refreshing ? (
                <CircularProgress size={20} sx={{ color: '#fff' }} />
              ) : (
                t('btn:check_again')
              )}
            </Button>
            <Typography
              sx={{
                fontSize: '11px',
                color: colors.textMuted,
                textAlign: 'center',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {t('maintenance_screen:auto_check')}
              {!!lastTry &&
                ` · ${t('maintenance_screen:last_try', { time: lastTry })}`}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default MaintenanceView
